import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calculator as CalcIcon } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { AMOUNT_OPTIONS } from "@/lib/data";

const TERMS = [24, 36, 48, 60];
const ANNUAL_RATE = 0.0995;

const formatCAD = (n) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);

const bucketFor = (amount) => {
  if (amount < 25000) return AMOUNT_OPTIONS[0];
  if (amount < 50000) return AMOUNT_OPTIONS[1];
  if (amount < 100000) return AMOUNT_OPTIONS[2];
  if (amount < 250000) return AMOUNT_OPTIONS[3];
  return AMOUNT_OPTIONS[4];
};

const Calculator = ({ onApply }) => {
  const [amount, setAmount] = useState(75000);
  const [term, setTerm] = useState(48);

  const monthly = useMemo(() => {
    const r = ANNUAL_RATE / 12;
    return (amount * r) / (1 - Math.pow(1 + r, -term));
  }, [amount, term]);

  const apply = () => {
    onApply({
      requestedAmount: bucketFor(amount),
      notes: `Simulation calculatrice : ${formatCAD(amount)} sur ${term} mois (≈ ${formatCAD(monthly)}/mois).`,
    });
    scrollToId("contact");
  };

  return (
    <section id="calculatrice" data-testid="calculator-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          caption="Calculatrice"
          title="Estimez votre mensualité en 10 secondes"
          subtitle="Une estimation indicative pour planifier votre acquisition. Les conditions dépendent de votre dossier — c'est notre travail d'obtenir les meilleures du marché."
        />

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-2xl border border-slate-900/10 bg-white spotlight-card p-8 lg:p-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-12">
            <div>
              <div className="flex items-center justify-between">
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Montant de l'équipement
                </label>
                <span data-testid="calculator-amount-display" className="font-display text-2xl font-bold text-sky-600">
                  {formatCAD(amount)}
                </span>
              </div>
              <Slider
                data-testid="calculator-amount-slider"
                value={[amount]}
                onValueChange={(v) => setAmount(v[0])}
                min={10000}
                max={500000}
                step={5000}
                className="mt-6"
              />
              <div className="flex justify-between mt-2 font-mono text-[11px] text-slate-9000">
                <span>10 000 $</span>
                <span>500 000 $</span>
              </div>

              <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mt-10">
                Durée du terme
              </p>
              <div className="grid grid-cols-4 gap-3 mt-4">
                {TERMS.map((t) => (
                  <button
                    key={t}
                    data-testid={`calculator-term-${t}`}
                    onClick={() => setTerm(t)}
                    className={`rounded-lg border px-3 py-3 text-sm font-bold transition-all duration-300 ${
                      term === t
                        ? "border-sky-400 bg-sky-400/15 text-sky-600"
                        : "border-slate-900/10 text-slate-500 hover:border-slate-900/30"
                    }`}
                  >
                    {t} mois
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-sky-400/30 bg-[#F7F9FC] p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 text-slate-500">
                  <CalcIcon className="w-4 h-4 text-sky-600" />
                  <p className="font-mono text-xs uppercase tracking-[0.2em]">Paiement mensuel estimé</p>
                </div>
                <motion.p
                  key={`${amount}-${term}`}
                  data-testid="calculator-monthly-payment"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="font-display text-6xl font-extrabold text-slate-900 mt-4"
                >
                  {formatCAD(monthly)}
                  <span className="text-xl text-slate-9000 font-semibold">/mois</span>
                </motion.p>
                <p className="text-xs text-slate-9000 mt-3 leading-relaxed">
                  Il s'agit d'un générateur de scénarios. Les conditions peuvent varier en fonction
                  de votre cote de crédit. Ne considérez pas ceci comme une offre, une approbation
                  ou une confirmation de quelque nature que ce soit.
                </p>
              </div>
              <button
                data-testid="calculator-apply-button"
                onClick={apply}
                className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 px-6 py-3.5 text-sm font-bold text-[#0A1220] hover:bg-sky-300 transition-colors duration-300"
              >
                Postuler avec ces critères
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Calculator;
